/**
 * Premium Metrics - System Impact with premium number styling
 * Clickable cards with expandable reveals
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, TrendingUp, DollarSign } from 'lucide-react';
import { MetricCounter } from './MetricCounter';
import { StaggerContainer, StaggerItem } from './ScrollReveal';
import { ScrollReveal } from './ScrollReveal';
import { RiskGlow } from './DecisionLayer';
import { cn } from '@/lib/utils';

interface Metric {
  icon: React.ComponentType<{ className?: string }>;
  value: { from: number; to: number; suffix?: string; prefix?: string };
  label: string;
  description: string;
  isRisk?: boolean;
  revealLines: [string, string];
}

const metrics: Metric[] = [
  {
    icon: Clock,
    value: { from: 0, to: 4.0, suffix: 'h' },
    label: 'Time Reclaimed',
    description: 'Average time reclaimed by removing decision friction.',
    revealLines: [
      'Decisions removed = hours returned to execution.',
      'Priority is computed, not debated.',
    ],
  },
  {
    icon: TrendingUp,
    value: { from: 0, to: 15, suffix: '%', prefix: '+' },
    label: 'Deals Closed',
    description: 'Additional deals closed due to faster follow-ups and reduced missed opportunities.',
    revealLines: [
      'Follow-ups happen at the right moment.',
      'The system reduces missed opportunities.',
    ],
  },
  {
    icon: DollarSign,
    value: { from: 0, to: 0 },
    label: 'Revenue Protected',
    description: 'Revenue at risk surfaced daily before it becomes lost revenue.',
    isRisk: true,
    revealLines: [
      'Risk is surfaced daily, before it becomes loss.',
      'Inaction is visible — and preventable.',
    ],
  },
];

export function PremiumMetrics() {
  const [expandedMetric, setExpandedMetric] = useState<number | null>(null);

  return (
    <section className="py-24 md:py-32 px-4 bg-background border-y border-border/30 relative overflow-hidden">
      {/* Risk glow for Revenue Protected */}
      <RiskGlow className="top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      <div className="container mx-auto max-w-5xl relative z-10">
        <ScrollReveal>
          <p className="text-sm md:text-base text-muted-foreground text-center mb-12 md:mb-16 max-w-xl mx-auto leading-[var(--leading-relaxed)] tracking-[var(--tracking-normal)]">
            The metrics below are the natural result of this daily loop.
          </p>
        </ScrollReveal>

        <StaggerContainer className="grid md:grid-cols-3 gap-[var(--gap-comfortable)] md:gap-[var(--gap-spacious)]" staggerDelay={0.2}>
          {metrics.map((metric, index) => {
            const Icon = metric.icon;
            const isDaily = metric.label === 'Revenue Protected';
            const isExpanded = expandedMetric === index;

            return (
              <StaggerItem key={index}>
                <motion.button
                  type="button"
                  onClick={() => setExpandedMetric(isExpanded ? null : index)}
                  className={cn(
                    'w-full p-6 md:p-8 dsos-card rounded-[var(--radius-card)] border bg-card h-full flex flex-col text-center relative overflow-hidden transition-all',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    metric.isRisk
                      ? 'border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent'
                      : 'border-border/40',
                    isExpanded && 'border-primary/40 bg-card shadow-lg'
                  )}
                  whileHover={{ y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                >
                  {/* Icon */}
                  <motion.div
                    className={cn(
                      'p-2 rounded-lg bg-primary/10 border border-primary/20 w-fit mx-auto mb-4',
                      metric.isRisk && 'bg-amber-500/10 border-amber-500/20'
                    )}
                    whileHover={{ rotate: 5, scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Icon className={cn('w-5 h-5', metric.isRisk ? 'text-amber-500' : 'text-primary')} />
                  </motion.div>

                  {/* Value */}
                  <div className="mb-4 min-h-[3rem] flex items-center justify-center">
                    {isDaily ? (
                      <p className="text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground tracking-[var(--tracking-tighter)] leading-[var(--leading-display)]">
                        Daily
                      </p>
                    ) : (
                      <MetricCounter
                        from={metric.value.from}
                        to={metric.value.to}
                        suffix={metric.value.suffix}
                        prefix={metric.value.prefix}
                      />
                    )}
                  </div>

                  {/* Label */}
                  <h3 className={cn(
                    'text-sm md:text-base font-semibold text-foreground mb-2',
                    metric.isRisk && 'text-amber-500'
                  )}>
                    {metric.label}
                  </h3>

                  {/* Description */}
                  <p className="text-xs md:text-sm text-muted-foreground leading-[var(--leading-body)] tracking-[var(--tracking-normal)] mb-4">
                    {metric.description}
                  </p>

                  {/* Expandable Reveal */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="mt-4 pt-4 border-t border-border/40 overflow-hidden"
                      >
                        <div className="space-y-2 text-xs md:text-sm text-muted-foreground leading-relaxed text-left">
                          <p>{metric.revealLines[0]}</p>
                          <p>{metric.revealLines[1]}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>
    </section>
  );
}
